import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCreateBlockNote,
  FormattingToolbar,
  FormattingToolbarController,
  getFormattingToolbarItems,
  useComponentsContext,
  useBlockNoteEditor,
  useEditorSelectionChange,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';
import { useTheme } from '../../context/ThemeContext';
import {
  getVision,
  getResource,
  updateResource,
  deleteResource,
  getResourceVersions,
  restoreResourceVersion,
} from '../../actions/planningAction';
import { flattenNodes, LinkPicker } from './PlanningLibrary';
import PlanningChat from './PlanningChat';
import './PlanningDocEditor.css';

// Standard ProseMirror algorithm: find the full range of a mark around a position.
function getMarkRangeAtPos($pos, markType) {
  const after = $pos.parent.childAfter($pos.parentOffset);
  if (!after.node) return null;
  const mark = after.node.marks.find(m => m.type === markType);
  if (!mark) return null;
  let startIndex = $pos.index();
  let startPos = $pos.start() + after.offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + after.node.nodeSize;
  while (startIndex > 0 && mark.isInSet($pos.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }
  while (endIndex < $pos.parent.childCount && mark.isInSet($pos.parent.child(endIndex).marks)) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return { from: startPos, to: endPos };
}

// "Remove link" toolbar button — only shows when the selection sits on a link.
function RemoveLinkButton() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  const [hasLink, setHasLink] = useState(false);

  useEditorSelectionChange(() => {
    setHasLink(!!editor.getSelectedLinkUrl());
  }, editor);

  if (!hasLink) return null;

  const removeLink = () => {
    editor.transact((tr) => {
      const linkType = editor.pmSchema.marks.link;
      if (!linkType) return;
      let { from, to, empty, $from } = tr.selection;
      if (empty) {
        const range = getMarkRangeAtPos($from, linkType);
        if (!range) return;
        from = range.from;
        to = range.to;
      }
      tr.removeMark(from, to, linkType);
    });
    setHasLink(false);
  };

  return (
    <Components.FormattingToolbar.Button
      mainTooltip="Remove link"
      label="Unlink"
      onClick={removeLink}
    />
  );
}

export default function PlanningDocEditor() {
  const { visionId, resourceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const { milestones } = useSelector(s => s.planning);

  const [resource, setResource] = useState(null);
  const [title, setTitle] = useState('');
  const [links, setLinks] = useState([]);
  const [linkedTodos, setLinkedTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [docHasContent, setDocHasContent] = useState(false);
  const [applyAction, setApplyAction] = useState(null); // 'Creating' | 'Updating' | null
  const [leaveConfirm, setLeaveConfirm] = useState(null); // { after: fn } | null
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const editor = useCreateBlockNote();
  const latest = useRef({ title: '', links: [] });
  const loadedRef = useRef(false);
  latest.current = { title, links };

  const allNodes = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map(n => [n._id, n])), [allNodes]);

  // Load resource + ensure milestones present for the link picker
  useEffect(() => {
    if (!milestones || milestones.length === 0) dispatch(getVision(visionId)).catch(() => {});
    setLoading(true);
    dispatch(getResource(resourceId))
      .then(async (d) => {
        setResource(d.resource);
        setTitle(d.resource.title || '');
        setLinks((d.resource.linkedNodeIds || []).map(String));
        setLinkedTodos(d.resource.linkedTodos || []);
        // Seed the editor from the stored markdown
        const md = d.resource.body || '';
        setDocHasContent(!!md.trim());
        if (md.trim()) {
          const blocks = await editor.tryParseMarkdownToBlocks(md);
          editor.replaceBlocks(editor.document, blocks);
          const last = editor.document[editor.document.length - 1];
          if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
        }
        loadedRef.current = true;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resourceId, visionId, dispatch]); // eslint-disable-line

  // Explicit save only — no autosave. A silent debounced save that could fire
  // before the document finished loading is what used to wipe documents.
  const persist = useCallback(async () => {
    if (!loadedRef.current) return false;
    const { title: t, links: l } = latest.current;
    let md = '';
    try { md = await editor.blocksToMarkdownLossy(editor.document); } catch (_) {}
    setSaving(true);
    try {
      const d = await dispatch(updateResource(resourceId, { title: t, body: md, linkedNodeIds: l }));
      setResource(d.resource);
      setSaved(true);
      setSaving(false);
      return true;
    } catch (_) {
      setSaving(false);
      return false;
    }
  }, [dispatch, resourceId, editor]);

  const markDirty = useCallback(() => {
    if (loadedRef.current) setSaved(false);
  }, []);

  // Tables can become the terminal block, leaving nowhere to type after them.
  // Guarantee a trailing empty paragraph so the user can always escape a table.
  const ensureTrailingBlock = useCallback(() => {
    const blocks = editor.document;
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'table') {
      editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
    }
  }, [editor]);

  const onEditorChange = () => {
    if (!loadedRef.current) return;
    ensureTrailingBlock();
    markDirty();
  };

  const toggleLink = (id) => {
    setLinks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    markDirty();
  };

  // Warn on tab close / refresh if there's unsaved work.
  useEffect(() => {
    const handler = (e) => {
      if (!saved && loadedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saved]);

  // Gate in-app navigation behind a save-or-discard prompt when there are
  // unsaved changes, instead of silently saving or silently losing them.
  const guardedNav = (fn) => {
    if (!saved && loadedRef.current) setLeaveConfirm({ after: fn });
    else fn();
  };

  const remove = async () => {
    try { await dispatch(deleteResource(resourceId)); } catch (_) {}
    navigate(`/dashboard/planning/${visionId}`);
  };

  const openAssist = async () => {
    if (!assistOpen && !saved) await persist(); // flush so Dmyor Assist reasons over the live doc
    setAssistOpen(v => !v);
  };

  // Apply an AI-produced document body into the editor and persist it.
  const applyDocBody = async (md, newTitle) => {
    const verb = docHasContent ? 'Updating' : 'Creating';
    setApplyAction(verb);
    try {
      // Let the overlay paint before the (synchronous) block replace.
      await new Promise(r => setTimeout(r, 50));
      if (md && md.trim()) {
        const blocks = await editor.tryParseMarkdownToBlocks(md);
        editor.replaceBlocks(editor.document, blocks);
        const last = editor.document[editor.document.length - 1];
        if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
        setDocHasContent(true);
      }
      if (newTitle && (!title.trim() || title.trim().toLowerCase() === 'untitled')) {
        latest.current = { ...latest.current, title: newTitle };
        setTitle(newTitle);
      }
      await persist();
    } finally {
      setApplyAction(null);
    }
  };

  const goBack = () => guardedNav(() => navigate(-1));

  const openHistory = async () => {
    setHistoryOpen(true);
    setVersionsLoading(true);
    try {
      const d = await dispatch(getResourceVersions(resourceId));
      setVersions(d.versions || []);
    } catch (_) {
      setVersions([]);
    }
    setVersionsLoading(false);
  };

  const restoreVersion = async (versionId) => {
    if (restoring) return;
    setRestoring(versionId);
    try {
      const d = await dispatch(restoreResourceVersion(resourceId, versionId));
      const r = d.resource;
      setResource(r);
      setTitle(r.title || '');
      const md = r.body || '';
      setDocHasContent(!!md.trim());
      const blocks = md.trim() ? await editor.tryParseMarkdownToBlocks(md) : [{ type: 'paragraph' }];
      editor.replaceBlocks(editor.document, blocks);
      setSaved(true);
      setHistoryOpen(false);
    } catch (_) {}
    setRestoring(null);
  };

  const WS_TABS = [
    { key: 'dashboard', label: 'Plan' },
    { key: 'execute',   label: 'Execute' },
    { key: 'habits',    label: 'Habits' },
    { key: 'library',   label: 'Library' },
    { key: 'journal',   label: 'Journal' },
  ];

  return (
    <div className={`pde-wrap${theme === 'light' ? ' pde-wrap--light' : ''}`}>
      {/* Workspace nav — a doc lives inside the Library, keep it reachable */}
      <div className="pde-ws-nav">
        <div className="pde-ws-toggle">
          {WS_TABS.map(t => (
            <button
              key={t.key}
              className={`pde-ws-btn${t.key === 'library' ? ' pde-ws-btn--active' : ''}`}
              onClick={() => guardedNav(() => navigate(`/dashboard/planning/${visionId}?view=${t.key}`))}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top bar */}
      <div className="pde-topbar">
        <button className="pde-back" onClick={goBack}>Back</button>
        <button
          className={`pde-save-btn${saved ? '' : ' pde-save-btn--dirty'}`}
          onClick={() => persist()}
          disabled={loading || saving || saved}
        >
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
        <button className="pde-history-btn" onClick={openHistory} disabled={loading} title="Version history">
          <i className="bx bx-history" /> History
        </button>
        <div className="pde-assist-wrap">
          <button
            className={`pde-generate${assistOpen ? ' pde-generate--open' : ''}`}
            onClick={openAssist}
            disabled={loading}
            title="Chat with Dmyor Assist about this document"
          >
            ✦ Dmyor Assist
          </button>
        </div>
        <button className="pde-del" onClick={() => setConfirmDelete(true)} title="Delete document">Delete</button>
      </div>

      {leaveConfirm && (
        <div className="pde-confirm-overlay" onMouseDown={() => setLeaveConfirm(null)}>
          <div className="pde-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="pde-confirm-title">Save changes before leaving?</h3>
            <p className="pde-confirm-text">This document has unsaved changes.</p>
            <div className="pde-confirm-actions">
              <button className="pde-confirm-cancel" onClick={() => setLeaveConfirm(null)}>Cancel</button>
              <button
                className="pde-confirm-discard"
                onClick={() => { const fn = leaveConfirm.after; setLeaveConfirm(null); fn(); }}
              >
                Discard
              </button>
              <button
                className="pde-confirm-save"
                onClick={async () => {
                  const fn = leaveConfirm.after;
                  const ok = await persist();
                  setLeaveConfirm(null);
                  if (ok) fn();
                }}
              >
                Save &amp; leave
              </button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="pde-confirm-overlay" onMouseDown={() => setHistoryOpen(false)}>
          <div className="pde-history-panel" onMouseDown={e => e.stopPropagation()}>
            <div className="pde-history-head">
              <h3 className="pde-confirm-title">Version history</h3>
              <button className="pde-history-close" onClick={() => setHistoryOpen(false)}>×</button>
            </div>
            {versionsLoading ? (
              <p className="pde-history-empty">Loading…</p>
            ) : versions.length === 0 ? (
              <p className="pde-history-empty">No earlier versions yet. A version is saved automatically every time you save changes.</p>
            ) : (
              <ul className="pde-history-list">
                {versions.map(v => (
                  <li key={v._id} className="pde-history-item">
                    <div className="pde-history-item-main">
                      <div className="pde-history-item-top">
                        <span className="pde-history-item-date">{new Date(v.savedAt).toLocaleString()}</span>
                        <span className="pde-history-item-title">{v.title || 'Untitled'}</span>
                      </div>
                      <p className="pde-history-item-preview">{v.preview ? v.preview : '(empty)'}</p>
                    </div>
                    <button
                      className="pde-history-restore"
                      onClick={() => restoreVersion(v._id)}
                      disabled={restoring === v._id}
                    >
                      {restoring === v._id ? 'Restoring…' : 'Restore'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="pde-confirm-overlay" onMouseDown={() => setConfirmDelete(false)}>
          <div className="pde-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="pde-confirm-title">Delete this document?</h3>
            <p className="pde-confirm-text">
              "{title || 'Untitled'}" will be permanently removed. This cannot be undone.
            </p>
            <div className="pde-confirm-actions">
              <button className="pde-confirm-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="pde-confirm-go" onClick={remove}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className={`pde-body${assistOpen ? ' pde-body--chat' : ''}`}>
      <div className="pde-inner">
        {/* Title */}
        <input
          className="pde-title"
          value={title}
          placeholder="Untitled document"
          onChange={e => { setTitle(e.target.value); markDirty(); }}
        />

        {/* Linked nodes */}
        <div className="pde-links">
          {links.map(id => {
            const n = nodeById[id];
            return (
              <span key={id} className="pde-chip">
                <span className={`pde-dot pde-dot--${n?.type || 'node'}`} />
                {n ? (n.content.length > 40 ? n.content.slice(0, 40) + '…' : n.content) : 'node'}
                <button className="pde-chip-x" onClick={() => toggleLink(id)}>×</button>
              </span>
            );
          })}
          <div className="pde-chip-add-wrap">
            <button className="pde-chip-add" onClick={() => setShowPicker(v => !v)}>+ Link node</button>
            {showPicker && (
              <LinkPicker allNodes={allNodes} selected={links} onToggle={toggleLink} onClose={() => setShowPicker(false)} />
            )}
          </div>
        </div>

        {/* Linked todos — jump back to the todo in Execute */}
        {linkedTodos.length > 0 && (
          <div className="pde-todos">
            <span className="pde-todos-label">From todo</span>
            {linkedTodos.map(t => (
              <button
                key={t._id}
                className={`pde-todo-chip${t.done ? ' pde-todo-chip--done' : ''}`}
                onClick={() => guardedNav(() => navigate(`/dashboard/planning/${visionId}?view=execute&todo=${t._id}`))}
                title="Go to this todo in Execute"
              >
                <i className="bx bx-list-check" />
                {t.text.length > 48 ? t.text.slice(0, 48) + '…' : t.text}
                <span className="pde-todo-chip-go">↗</span>
              </button>
            ))}
          </div>
        )}

        {/* WYSIWYG editor */}
        <div className="pde-editor-shell">
          {loading && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-doc-spinner" />
                <div className="pde-thinking-sub">Loading document…</div>
              </div>
            </div>
          )}
          {applyAction && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-thinking-spark">✦</span>
                <div className="pde-thinking-text">
                  {applyAction} your document
                  <span className="pde-thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                </div>
                <div className="pde-thinking-sub">
                  {applyAction === 'Creating' ? 'Writing it into the editor' : 'Applying the changes'}
                </div>
              </div>
            </div>
          )}
          <BlockNoteView
            editor={editor}
            theme={theme === 'light' ? 'light' : 'dark'}
            onChange={onEditorChange}
            formattingToolbar={false}
          >
            <FormattingToolbarController
              formattingToolbar={() => (
                <FormattingToolbar>
                  {getFormattingToolbarItems()}
                  <RemoveLinkButton key="removeLink" />
                </FormattingToolbar>
              )}
            />
          </BlockNoteView>
        </div>
      </div>

      {/* Dmyor Assist chat — the same chat used everywhere else, with this
          document in focus so it can discuss it and write/revise it directly. */}
      {assistOpen && (
        <div className="pde-chat-drawer">
          <button className="pde-chat-close--floating" onClick={() => setAssistOpen(false)} title="Close">✕</button>
          <div className="pde-chat-embed">
            <PlanningChat
              visionId={visionId}
              milestones={milestones}
              resourceId={resourceId}
              onDocUpdate={({ title: t, body }) => applyDocBody(body, t)}
              onRefresh={() => dispatch(getVision(visionId)).catch(() => {})}
              onFocusNode={(id) => guardedNav(() => navigate(`/dashboard/planning/${visionId}?view=dashboard&focus=${id}`))}
              theme={theme}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
